import { useState } from 'react';
import { familyApi } from '../../services/api';

const EmbeddedFamilyCreation = ({ onComplete, onBack, isLoading, setIsLoading }) => {
  const [familyName, setFamilyName] = useState('');
  const [familyDescription, setFamilyDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      setError('Please enter a family name');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('🔍 Creating family with data:', { name: familyName.trim(), description: familyDescription.trim() });
      
      const response = await familyApi.createFamily({
        name: familyName.trim(),
        description: familyDescription.trim() || undefined
      });

      console.log('🔍 Family creation response:', response.data);

      // API returns { message, family } on success
      if (response.data.family) {
        setSuccess(true);
        // Wait a moment to show success message, then complete
        setTimeout(() => {
          onComplete({ 
            action: 'create_family',
            familyId: response.data.family.id,
            family: response.data.family 
          });
        }, 1500);
      } else {
        setError('Unexpected response from server. Please try again.');
      }
    } catch (error) {
      console.error('Failed to create family:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to create family. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="family-success">
        <div className="success-icon">✅</div>
        <h3>Family Created Successfully!</h3>
        <p>Your family "{familyName}" has been created. You can now invite others to join.</p>
        <div className="family-info">
          <p><strong>Family Name:</strong> {familyName}</p>
          {familyDescription && <p><strong>Description:</strong> {familyDescription}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="embedded-family-creation">
      <div className="creation-header">
        <div className="creation-icon">👨‍👩‍👧‍👦</div>
        <h3>Create Your Family</h3>
        <p>Set up your family group to track reading progress together</p>
      </div>

      <div className="creation-form">
        <div className="form-group">
          <label htmlFor="familyName">Family Name *</label>
          <input
            type="text"
            id="familyName"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder="Enter your family name"
            className="form-input"
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="familyDescription">Description (Optional)</label>
          <textarea
            id="familyDescription"
            value={familyDescription}
            onChange={(e) => setFamilyDescription(e.target.value)}
            placeholder="Tell us about your family..."
            className="form-textarea"
            rows="3"
            disabled={isLoading}
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onBack}
            disabled={isLoading}
          >
            Back
          </button>
          <button
            className="btn-primary"
            onClick={handleCreateFamily}
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Family'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .embedded-family-creation {
          max-width: 500px;
          margin: 0 auto;
        }

        .creation-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .creation-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .creation-header h3 {
          margin: 0 0 8px 0;
          color: var(--color-text-primary);
          font-size: 20px;
        }

        .creation-header p {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: 14px;
        }

        .creation-form {
          background: var(--color-background);
          padding: 24px;
          border-radius: 12px;
          border: 1px solid var(--color-border);
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: var(--color-text-primary);
          font-size: 14px;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--color-border-strong);
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--color-primary-light);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary-light) 10%, transparent);
        }

        .form-input:disabled,
        .form-textarea:disabled {
          background: var(--color-surface-elevated);
          cursor: not-allowed;
        }

        .error-message {
          background: var(--color-error-light);
          color: var(--color-error);
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          border: 1px solid var(--color-error-border);
        }

        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .family-success {
          text-align: center;
          padding: 40px 20px;
        }

        .success-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .family-success h3 {
          margin: 0 0 16px 0;
          color: var(--color-success);
          font-size: 20px;
        }

        .family-success p {
          margin-bottom: 24px;
          color: var(--color-text-secondary);
        }

        .family-info {
          background: var(--color-success-light);
          padding: 16px;
          border-radius: 8px;
          border: 1px solid var(--color-success-border);
          text-align: left;
        }

        .family-info p {
          margin: 0 0 8px 0;
          font-size: 14px;
        }

        .family-info p:last-child {
          margin-bottom: 0;
        }

        @media (max-width: 480px) {
          .creation-form {
            padding: 20px;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default EmbeddedFamilyCreation;
